import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import {
  Badge,
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Grid,
  HStack,
  PasswordFormatHelperText,
  PasswordInput,
  Separator,
  Stack,
  Text,
  toaster,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { PASSWORD_FORMAT_REGEX } from '@/constants/passwordFormatRegex'
import { ApiError, useMutation } from '@/lib/api'
import { updatePassword } from '@/lib/auth'

type Props = {
  trigger: React.ReactNode
}

const changePasswordFormSchema = z
  .object({
    oldPassword: z.string().min(1, ERROR_MESSAGES.passwordRequired),
    newPassword: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
    confirmNewPassword: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
  })
  .refine(data => data.oldPassword !== data.newPassword, {
    message: ERROR_MESSAGES.sameAsCurrentPassword,
    path: ['newPassword'],
  })
  .refine(data => data.newPassword === data.confirmNewPassword, {
    message: ERROR_MESSAGES.newPasswordMismatch,
    path: ['confirmNewPassword'],
  })

type FormValue = z.infer<typeof changePasswordFormSchema>

export const ChangePasswordDialog = ({ trigger }: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValue>({
    resolver: zodResolver(changePasswordFormSchema),
    mode: 'onBlur',
  })
  const [open, setOpen] = useState(false)
  const [apiError, setApiError] = useState<ApiError>()

  const { request, isPending } = useMutation(updatePassword, {
    onSuccess: () => {
      setOpen(false)
      toaster.create({
        type: 'success',
        title: 'パスワードを変更しました。',
      })
    },
    onError: setApiError,
  })

  const sameAsCurrentPasswordError =
    errors?.newPassword?.message === ERROR_MESSAGES.sameAsCurrentPassword
      ? errors.newPassword
      : undefined
  const confirmNewPasswordMismatchError =
    errors?.confirmNewPassword?.message === ERROR_MESSAGES.newPasswordMismatch
      ? errors.confirmNewPassword
      : undefined

  return (
    <DialogRoot
      size="xl"
      open={open}
      onOpenChange={e => {
        reset()
        setOpen(e.open)
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <form
          onSubmit={handleSubmit(async data => {
            await request({
              oldPassword: data.oldPassword,
              newPassword: data.newPassword,
            })
          })}
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <DialogHeader p="6">
            <DialogTitle>パスワード変更</DialogTitle>
          </DialogHeader>
          <Separator />
          <Box flex="1 1 auto" overflow="auto">
            <DialogBody px="6" pt="4" pb="8">
              <Text>
                新しいパスワードを設定します。現在のパスワードと新しいパスワードを入力し、「パスワードを変更」ボタンを押してください。
              </Text>
              <Box w="full" mt="8">
                <Stack gap="12" align="center" w="full">
                  <Grid
                    maxW="2xl"
                    w="full"
                    rowGap="7"
                    columnGap="6"
                    templateColumns={{
                      base: '1fr',
                      desktop: 'max-content 1fr',
                    }}
                  >
                    <FieldRoot
                      display="grid"
                      alignItems="start"
                      gridTemplateColumns="subgrid"
                      gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                      gap={{ base: '1', desktop: '6' }}
                      invalid={!!errors.oldPassword}
                    >
                      <HStack
                        w="full"
                        justify={{
                          base: 'flex-start',
                          desktop: 'space-between',
                        }}
                        gap={{ base: '1', desktop: '4' }}
                      >
                        <FieldLabel
                          fontSize="md"
                          fontWeight="normal"
                          color="text.sub"
                          h="10"
                        >
                          現在のパスワード
                        </FieldLabel>
                        <Badge variant="subtle" colorPalette="red">
                          必須
                        </Badge>
                      </HStack>
                      <Stack gap="0.5">
                        <PasswordInput
                          {...register('oldPassword')}
                          w="full"
                          autoComplete="current-password"
                          placeholder="パスワードを入力してください"
                        />
                        <FieldErrorText>
                          {errors.oldPassword?.message}
                        </FieldErrorText>
                      </Stack>
                    </FieldRoot>
                    <FieldRoot
                      display="grid"
                      alignItems="start"
                      gridTemplateColumns="subgrid"
                      gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                      gap={{ base: '1', desktop: '6' }}
                      invalid={
                        !!errors.newPassword && !sameAsCurrentPasswordError
                      }
                    >
                      <HStack
                        w="full"
                        justify={{
                          base: 'flex-start',
                          desktop: 'space-between',
                        }}
                        gap={{ base: '1', desktop: '4' }}
                      >
                        <FieldLabel
                          fontSize="md"
                          fontWeight="normal"
                          color="text.sub"
                          h="10"
                        >
                          新しいパスワード
                        </FieldLabel>
                        <Badge variant="subtle" colorPalette="red">
                          必須
                        </Badge>
                      </HStack>
                      <Stack gap="0.5">
                        <PasswordInput
                          {...register('newPassword')}
                          w="full"
                          autoComplete="new-password"
                          placeholder="パスワードを入力してください"
                        />
                        {(!errors.newPassword ||
                          !!sameAsCurrentPasswordError) && (
                          <PasswordFormatHelperText />
                        )}
                        <FieldErrorText>
                          {errors.newPassword?.message}
                        </FieldErrorText>
                      </Stack>
                    </FieldRoot>
                    <FieldRoot
                      display="grid"
                      alignItems="start"
                      gridTemplateColumns="subgrid"
                      gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                      gap={{ base: '1', desktop: '6' }}
                      invalid={
                        !!errors.confirmNewPassword &&
                        !confirmNewPasswordMismatchError
                      }
                    >
                      <HStack
                        w="full"
                        justify={{
                          base: 'flex-start',
                          desktop: 'space-between',
                        }}
                        gap={{ base: '1', desktop: '4' }}
                      >
                        <FieldLabel
                          fontSize="md"
                          fontWeight="normal"
                          color="text.sub"
                          h="10"
                        >
                          新しいパスワード（確認）
                        </FieldLabel>
                        <Badge variant="subtle" colorPalette="red">
                          必須
                        </Badge>
                      </HStack>
                      <Stack gap="0.5">
                        <PasswordInput
                          {...register('confirmNewPassword')}
                          w="full"
                          placeholder="パスワードを入力してください"
                          autoComplete="new-password"
                        />
                        {(!!confirmNewPasswordMismatchError ||
                          !errors.confirmNewPassword) && (
                          <PasswordFormatHelperText />
                        )}
                        <FieldErrorText>
                          {errors.confirmNewPassword?.message}
                        </FieldErrorText>
                      </Stack>
                    </FieldRoot>
                    {(!!apiError ||
                      !!confirmNewPasswordMismatchError ||
                      !!sameAsCurrentPasswordError) && (
                      <HStack
                        gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                        gap="3"
                        justify="center"
                        color="text.error"
                      >
                        <AlertErrorIcon />
                        <Text fontWeight="normal">
                          {apiError
                            ? apiError.message
                            : sameAsCurrentPasswordError
                              ? sameAsCurrentPasswordError.message
                              : confirmNewPasswordMismatchError?.message}
                        </Text>
                      </HStack>
                    )}
                  </Grid>
                </Stack>
              </Box>
            </DialogBody>
          </Box>
          <Box flex="0 0 auto">
            <Separator />
            <DialogFooter p="6">
              <DialogActionTrigger asChild>
                <Button variant="solid" colorPalette="gray">
                  キャンセル
                </Button>
              </DialogActionTrigger>
              <Button loading={isPending} type="submit" disabled={!isValid}>
                パスワードを変更
              </Button>
            </DialogFooter>
          </Box>
          <DialogCloseTrigger />
        </form>
      </DialogContent>
    </DialogRoot>
  )
}
